import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subscription, interval } from 'rxjs';
import { ConfigService } from './config.service';
import { UserScopedStorageService } from '../shared/services/user-scoped-storage.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { AiBannerComponent } from '../admin/aiBanner/ai-banner-component';

const LAST_SHOWN_KEY = 'ai_banner_last_shown';
const REPEAT_INTERVAL_MS = 24 * 60 * 60 * 1000;
const WATCHER_TICK_MS = 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class BannerService {
  private showBannerSubject = new BehaviorSubject<boolean>(false);
  public showBanner$ = this.showBannerSubject.asObservable();

  private lastShownAt: number | null = null; // fallback when useStorage is off
  private watcherSub: Subscription | null = null;
  private bannerOpen = false;

  constructor(
    public config: ConfigService,
    private dialog: MatDialog,
    private userStorage: UserScopedStorageService,
    private router: Router,
  ) {}

  /**
   * Check if banner should be displayed
   * @returns true if banner should be shown
   */
  shouldShowBanner(): boolean {
    return !!this.config.appConfig.setup.enableAIBanner;
  }

  checkAndShowBanner(): void {
    const shouldShow = this.shouldShowBanner();
    this.showBannerSubject.next(shouldShow);

    if (shouldShow) {
      this.openBannerModal();
      this.startDailyWatcher();
    }
  }

  /**
   * Show the banner only if it was never shown or last shown 24h+ ago.
   * Used on session restore (page refresh) and by the daily watcher.
   */
  checkAndShowBannerIfDue(): void {
    if (!this.shouldShowBanner()) {
      return;
    }
    if (this.isDue()) {
      this.showBannerSubject.next(true);
      this.openBannerModal();
    }
    this.startDailyWatcher();
  }

  stopDailyWatcher(): void {
    this.watcherSub?.unsubscribe();
    this.watcherSub = null;
  }

  private startDailyWatcher(): void {
    if (this.watcherSub) {
      return;
    }
    this.watcherSub = interval(WATCHER_TICK_MS).subscribe(() => {
      if (this.shouldShowBanner() && this.isDue()) {
        this.showBannerSubject.next(true);
        this.openBannerModal();
      }
    });
  }

  private isDue(): boolean {
    const lastShown = this.getLastShownAt();
    return lastShown === null || Date.now() - lastShown >= REPEAT_INTERVAL_MS;
  }

  private getLastShownAt(): number | null {
    const stored = this.userStorage.getItem(LAST_SHOWN_KEY);
    if (stored) return Number(stored);
    return this.lastShownAt;
  }

  private stampLastShown(): void {
    const now = Date.now();
    this.lastShownAt = now;
    this.userStorage.setItem(LAST_SHOWN_KEY, String(now));
  }

  /**
   * Open the AI banner modal
   */
  private openBannerModal(): void {
    if (this.bannerOpen) {
      return;
    }
    // The in-app disclaimer must never show on the login screen. The daily
    // watcher and the deferred post-login triggers can otherwise fire here
    // after a redirect that bypassed authLogout (401, cross-tab logout,
    // expiry) — so bail, and stop the leaked watcher while we are at it.
    if (this.router.url.startsWith('/login')) {
      this.stopDailyWatcher();
      return;
    }
    this.bannerOpen = true;
    this.stampLastShown();

    const dialogRef = this.dialog.open(AiBannerComponent, {
      disableClose: false,
      closeOnNavigation: true,
      panelClass: 'ai-banner-dialog',
    });

    dialogRef.afterClosed().subscribe(() => {
      this.bannerOpen = false;
      this.dismissBanner();
    });
  }

  dismissBanner(): void {
    this.showBannerSubject.next(false);
  }
}
